/*
  bf_turnmove.ts
  Copyright (c) 2013-present Human Cube Inc.

  Does all turn move calculation, etc.  Must all be synchronous and not involve any external DB
  or file access.  Also does not access any external user data.
  These restrictions are to allow it to be re-used in other environments such as the client.

  Notes:
    - Extra checks and states are in place to prevent a player from using an invalid move to delay
      the movement of a force into a later move stage (the 'invalid' dictionary assists with this).
*/

import BF from '../bfconst1';
import bfH from '../bf_helper';
import { Game } from '../gameTypes';
import { confirmOrFixXY } from '../bf_hitmap';
import bfTurnReplay from './bf_turnreplay';


interface Blocked {
  [forceID: number] : boolean;  // Indexed by forceID. This force is blocked.
}

interface UnloadedCargo {
  [forceID: number] : boolean;  // Indexed by forceID. This force unloaded cargo this turn.
}

interface Invalid {
  [forceID: number] : boolean;  // Indexed by forceID. Invalid move attempted by force, not just
                                // blocked, prevent any further movement of any kind for this force.
}

interface Obstruct {
  // Obstruction and blocked states for all forces.
  air: Uint32Array;      // Indexed by zoneID, 1 bit per player per zone.
  surface: Uint32Array;  // Indexed by zoneID, 1 bit per player per zone.
  blocked: Blocked;      // Indexed by forceID. This force is blocked.
  unloadedCargo: UnloadedCargo;  // Indexed by forceID. This force unloaded cargo this turn.
  invalid: Invalid;   // Indexed by forceID. Prohibit any kind of further movement for this force.
};


export const doArrange = (game: Game, turns: any) => {
  for(let i = 1; i <= game.players; i++) {
    if(!game.finished[i][0]) { // Player still in the game.
      if(turns[i] && turns[i].arrange && bfH.isObject(turns[i].arrange)) {
        for(let key in turns[i].arrange) {
          const forceID = bfH.convertToInt(key);
          const item = turns[i].arrange[key];
          if( forceID && item && Array.isArray(item) && item.length === 2 ) {
            const j = bfH.getPlayerForceIndex(game, i, forceID);
            if(j >= 0) {
              const zoneID = game.force[i][j][BF.FORCE_INDEX_ZONE];
              if(zoneID > 0 && zoneID <= game.zones) {
                const pos = confirmOrFixXY(game, zoneID, item[0], item[1]);
                bfTurnReplay.arrange(game, forceID, pos.x, pos.y);
                game.force[i][j][BF.FORCE_INDEX_X] = pos.x;
                game.force[i][j][BF.FORCE_INDEX_Y] = pos.y;
              }
            }
          }
        }
      }
    }
  }
};

export const doMove = (game: Game, turns: any) => {
  const obstruct: Obstruct = initObstructions(game);

  for(let moveCycle = 1; moveCycle <= BF.FORCE_MAX_MOVES; moveCycle++) {
    fillObstructions(game, obstruct);
    blockObstructedForces(game, obstruct, (moveCycle === 1));

    for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
      if(!game.finished[playerIndex][0] &&  // Player still in the game.
          turns[playerIndex] && turns[playerIndex].move &&
          bfH.isObject(turns[playerIndex].move)) {
        doPlayerMoveCycle(game, turns[playerIndex].move, playerIndex, moveCycle, obstruct);
      }
    }
  }
};

export const setAllMovementPoints = (game: Game) => {
  const obstruct: Obstruct = initObstructions(game);
  fillObstructions(game, obstruct);
  blockObstructedForces(game, obstruct, true);

  for(let playerIndex = 1; playerIndex <= game.players; playerIndex++) {
    for(let j = 0, len = game.force[playerIndex].length; j < len; j++) {
      const force = game.force[playerIndex][j];
      const zoneID = force[BF.FORCE_INDEX_ZONE];
      if( zoneID > 0 && zoneID <= game.zones ) {  // Alive normal unit.
        const forceID = force[BF.FORCE_INDEX_ID];
        if( !obstruct.blocked[forceID] ) {
          const forceType = force[BF.FORCE_INDEX_TYPE];
          force[BF.FORCE_INDEX_MOVE_POINTS] = game.forceRange[forceType];
          // If not blocked, then also clear the retreat option:
          force[BF.FORCE_INDEX_ZONE_RETREAT] = 0;
        }
        else force[BF.FORCE_INDEX_MOVE_POINTS] = 0;
      }
    }
  }
};

function initObstructions (game: Game): Obstruct {
  const len = game.zones;
  const obstruct: Obstruct = {
    air: new Uint32Array(len + 1),      // All entries start as 0.
    surface: new Uint32Array(len + 1),  // All entries start as 0.
    blocked: {},        // Nothing is blocked, yet.
    unloadedCargo: {},  // Nothing unloaded cargo, yet.
    invalid: {},        // Nothing is invalid, yet.
  };
  return obstruct;
}

function fillObstructions (game: Game, obstruct: Obstruct) {
  const len = game.zones;
  obstruct.air = new Uint32Array(len + 1);      // Reset all entries start as 0.
  obstruct.surface = new Uint32Array(len + 1);  // Reset all entries start as 0.
  // But keep current obstruct.blocked values as a starting point.

  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) {  // all players and 0.
    for(let j = 0; j < game.force[playerIndex].length; j++) {
      let zoneID = game.force[playerIndex][j][BF.FORCE_INDEX_ZONE];
      if(zoneID > 0 && zoneID <= game.zones) {
        let forceType = game.force[playerIndex][j][BF.FORCE_INDEX_TYPE];
        let forceFlags = game.forceFlags[forceType];
        if(forceFlags & BF.FORCE_FLAG_BLOCKS_SURFACE)
          obstruct.surface[zoneID] |= (1 << playerIndex);
        if(forceFlags & BF.FORCE_FLAG_BLOCKS_AIR)
          obstruct.air[zoneID] |= (1 << playerIndex);
      }
    }
  }
}

function blockObstructedForces (game: Game, obstruct: Obstruct, clearFlag: boolean) {
  if(clearFlag) {
    obstruct.blocked = {};   // Clear it. Set as nothing is blocked.
  }
  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) {  // all players and 0.
    let bitTest = ~game.fixedAlly[playerIndex];
    for(let j = 0; j < game.force[playerIndex].length; j++) {
      let forceID = game.force[playerIndex][j][BF.FORCE_INDEX_ID];
      let zoneID = game.force[playerIndex][j][BF.FORCE_INDEX_ZONE];
      if(zoneID <= 0 || zoneID > game.zones) {  // Purchased or destroyed are blocked.
        obstruct.blocked[forceID] = true;
      }
      else {
        if((game.flags & BF.GAME_FLAG_START_UNBLOCKED) === 0 || !clearFlag) {
          let forceType = game.force[playerIndex][j][BF.FORCE_INDEX_TYPE];
          let forceFlags = game.forceFlags[forceType];
          if(forceFlags & BF.FORCE_FLAG_CAN_BE_BLOCKED_BY_AIR) {
            if(obstruct.air[zoneID] & bitTest) {
              obstruct.blocked[forceID] = true;  // hostile air blocker, block further movement.
            }
          }
          if(forceFlags & BF.FORCE_FLAG_CAN_BE_BLOCKED_BY_SURFACE) {
            if(obstruct.surface[zoneID] & bitTest) {
              obstruct.blocked[forceID] = true;  // hostile land blocker, block further movement.
            }
          }
        }
      }
    }
  }
}

function doPlayerMoveCycle (
    game: Game, turnMove: any, playerIndex: number, moveCycle: number, obstruct: Obstruct,
) {
  // Execute a single complete move cycle for a player.
  for(let key in turnMove) {
    const forceID = bfH.convertToInt(key);
    if(obstruct.invalid[forceID]) {
      continue;  // Force has been flagged as given an invalid move. Prevent any movement.
    }
    const item = turnMove[key];

    // console.log('move: ' + moveCycle);
    // console.log(JSON.stringify(item));

    if(forceID && item && Array.isArray(item) && item.length >= moveCycle * 4) {
      const forceIndex = bfH.getPlayerForceIndex(game, playerIndex, forceID);
      if(forceIndex >= 0) {
        const zoneID = item[(moveCycle - 1) * 4 + 0];
        const x = item[(moveCycle - 1) * 4 + 1];
        const y = item[(moveCycle - 1) * 4 + 2];
        const extra = item[(moveCycle - 1) * 4 + 3];

        if(extra === 0)
          doNormalMove(game, forceIndex, forceID, zoneID, x, y,
                      playerIndex, moveCycle, obstruct);
        else if(extra === -1)
          doTransportLoadMove(game, forceIndex, forceID, zoneID, x, y,
                              playerIndex, moveCycle, obstruct);
        else if(extra > 0)
          doTransportUnloadMove(game, forceIndex, forceID, zoneID, x, y, extra,
                                playerIndex, moveCycle, obstruct);
        else{
          // Invalid move. Block further any type of further movement for this force.
          obstruct.invalid[forceID] = true;
        }
      }
    }
  }
}

function doNormalMove (
  game: Game, forceIndex: number, forceID: number, zoneID: number, x: number, y: number,
  playerIndex: number, moveCycle: number, obstruct: Obstruct,
) {
  // Do a single normal movement for a given force.
  const force = game.force[playerIndex][forceIndex];
  const forceType = force[BF.FORCE_INDEX_TYPE];

  if(zoneID <= 0 || zoneID > game.zones) {
    // Invalid zoneID requested to move to. Prevent further any movement for this force.
    obstruct.invalid[forceID] = true;
    return;
  }

  if(force[BF.FORCE_INDEX_MOVE_POINTS] === 0) {
    if(zoneID > 0 && zoneID === force[BF.FORCE_INDEX_ZONE_RETREAT] && moveCycle === 1) {
      // Only first movement cycle allowed for retreats.
      const pos = confirmOrFixXY(game, zoneID, x, y);
      bfTurnReplay.retreat(game, forceID, zoneID, pos.x, pos.y);
      force[BF.FORCE_INDEX_ZONE] = zoneID;
      force[BF.FORCE_INDEX_X] = pos.x;
      force[BF.FORCE_INDEX_Y] = pos.y;
      force[BF.FORCE_INDEX_MOVE_POINTS] = 0;   // Should already be 0, but just for reference.
      force[BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Cannot retreat from a retreat.
      obstruct.blocked[forceID] = true;   // Not allowed to move further after retreat.
      return;
    }
    // Invalid retreat or invalid move, prevent any further movement for this force:
    obstruct.invalid[forceID] = true;
    return;
  }

  if(obstruct.blocked[forceID]) {
    // Move blocked due to enemy forces.
    bfTurnReplay.moveBlocked(game, moveCycle, forceID, force[BF.FORCE_INDEX_ZONE], zoneID);
    return;
  }

  let doMove = false;

  if(bfH.isLand(game, zoneID)) {
    if(game.forceFlags[forceType] & BF.FORCE_FLAG_CAN_MOVE_ON_LAND)
      if(bfH.isLandNeighbour(game, force[BF.FORCE_INDEX_ZONE], zoneID))
        doMove = true;
  }
  else if(bfH.isWater(game, zoneID)) {
    if(game.forceFlags[forceType] & BF.FORCE_FLAG_CAN_MOVE_ON_WATER)
      if(bfH.isWaterNeighbour(game, force[BF.FORCE_INDEX_ZONE], zoneID))
        doMove = true;
  }

  if(!doMove) {
    // Requested zone was not an appropriate neighbour, or force cannot move to that zone type.
    obstruct.invalid[forceID] = true;  // Invalid move request, prevent further force movement.
    return;
  }

  const pos = confirmOrFixXY(game, zoneID, x, y);
  bfTurnReplay.move(game, moveCycle, forceID, zoneID, pos.x, pos.y);

  // This is later adjusted in doRetreatValuesCleanUp() at the end of the turn processing:
  force[BF.FORCE_INDEX_ZONE_RETREAT] = force[BF.FORCE_INDEX_ZONE];

  force[BF.FORCE_INDEX_ZONE] = zoneID;
  force[BF.FORCE_INDEX_X] = pos.x;
  force[BF.FORCE_INDEX_Y] = pos.y;
  force[BF.FORCE_INDEX_MOVE_POINTS]--;
}

function doTransportLoadMove (
  game: Game, forceIndex: number, forceID: number, cargoID: number, x: number, y: number,
  playerIndex: number, moveCycle: number, obstruct: Obstruct,
) {
  // Do a single load transport movement.

  if(obstruct.invalid[cargoID]) {
    return;  // The cargo force has been flagged as given an invalid move. Prevent any movement.
  }

  if(obstruct.unloadedCargo[forceID]) {
    // Transport or lifter has already unloaded one or more this turn. It is invalid to load after.
    obstruct.invalid[cargoID] = true;
    obstruct.invalid[forceID] = true;
    return;
  }

  const force = game.force[playerIndex][forceIndex];
  const forceType = force[BF.FORCE_INDEX_TYPE];

  let cargoIndex = bfH.getPlayerForceIndex(game, playerIndex, cargoID);
  if(cargoIndex < 0) {
    console.log('Invalid move, unable to find cargo to load.');
    obstruct.invalid[forceID] = true;  // Prevent any further movement for the transport.
    return;
  }

  const cargoForce = game.force[playerIndex][cargoIndex];
  const cargoType = cargoForce[BF.FORCE_INDEX_TYPE];
  const cargoSpace = game.forceCargoSpace[cargoType];
  const liftSpace = game.forceLiftSpace[cargoType];

  if(obstruct.blocked[cargoID]) {
    obstruct.blocked[forceID] = true;  // Cargo is blocked, so also block transport.
    bfTurnReplay.loadBlocked(game, moveCycle, cargoID, forceID,
                            cargoForce[BF.FORCE_INDEX_ZONE], force[BF.FORCE_INDEX_ZONE]);
    return;
  }

  if(cargoForce[BF.FORCE_INDEX_MOVE_POINTS] < game.forceRange[cargoType]) {
    // Cargo has moved, so load move request is invalid.
    // Prevent any further movement of any kind for both the cargo and transport.
    obstruct.invalid[cargoID] = true;
    obstruct.invalid[forceID] = true;
    return;
  }

  if(obstruct.unloadedCargo[forceID]) {
    // Invalid move request, transport has already unloaded something, so loading is prohibited.
    // Prevent any further movement of any kind for both the cargo and transport.
    obstruct.invalid[cargoID] = true;  
    obstruct.invalid[forceID] = true;
    return;
  }

  let wasBlocked = false;

  if(cargoSpace >= 0 && force[BF.FORCE_INDEX_LOAD_POINTS] >= cargoSpace) {
    // Transport has enough cargo space.
    if( (game.forceFlags[forceType] & BF.FORCE_FLAG_CAN_LOAD_WHEN_BLOCKED) ||
        !obstruct.blocked[forceID] ) {
      // The transport is allowed to load.
      let doMove = false;

      if( game.forceFlags[forceType] & BF.FORCE_FLAG_LOAD_SAME_ZONE ) {
        if( force[BF.FORCE_INDEX_ZONE] === cargoForce[BF.FORCE_INDEX_ZONE] ) {
          doMove = true;
        }
      }
      else { // Can only load from neighbour zone.
        if( bfH.isNeighbour(game, cargoForce[BF.FORCE_INDEX_ZONE], force[BF.FORCE_INDEX_ZONE]) ) {
          doMove = true;
        }
      }
      if( doMove ) {
        // Set as loaded on transport:
        let newZone = BF.FORCE_ZONE_CARGO - forceID;
        bfTurnReplay.move(game, moveCycle,
                          cargoForce[BF.FORCE_INDEX_ID],
                          newZone,
                          force[BF.FORCE_INDEX_X],
                          force[BF.FORCE_INDEX_Y]);
        force[BF.FORCE_INDEX_LOAD_POINTS] -= cargoSpace;
        cargoForce[BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Cargo cannot retreat from load.
        cargoForce[BF.FORCE_INDEX_ZONE] = newZone;
        cargoForce[BF.FORCE_INDEX_X] = 0;
        cargoForce[BF.FORCE_INDEX_Y] = 0;
        cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;
        return;
      }
    }
    else wasBlocked = true;
  }

  if(liftSpace >= 0 && force[BF.FORCE_INDEX_LIFT_POINTS] >= liftSpace) {
    if( (game.forceFlags[forceType] & BF.FORCE_FLAG_CAN_LOAD_WHEN_BLOCKED) ||
        !obstruct.blocked[forceID] ) {
      let doMove = false;
      if( game.forceFlags[forceType] & BF.FORCE_FLAG_LOAD_SAME_ZONE ) {
        if( force[BF.FORCE_INDEX_ZONE] === cargoForce[BF.FORCE_INDEX_ZONE] ) {
          doMove = true;
        }
      }
      else{
        if( bfH.isNeighbour(game, cargoForce[BF.FORCE_INDEX_ZONE], force[BF.FORCE_INDEX_ZONE]) ) {
          doMove = true;
        }
      }
      if( doMove ) {
        // Set as loaded on lift:
        let newZone = BF.FORCE_ZONE_CARGO - forceID;
        bfTurnReplay.move(game, moveCycle,
                          cargoForce[BF.FORCE_INDEX_ID],
                          newZone, 0, 0);
        force[BF.FORCE_INDEX_LIFT_POINTS] -= liftSpace;
        cargoForce[BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Cargo cannot retreat.
        cargoForce[BF.FORCE_INDEX_ZONE] = newZone;
        cargoForce[BF.FORCE_INDEX_X] = 0;
        cargoForce[BF.FORCE_INDEX_Y] = 0;
        cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;
        return;
      }
    }
    else {
      wasBlocked = true;
    }
  }

  if( wasBlocked ) {
    // Transport force could not load while blocked.
    bfTurnReplay.loadBlocked(game, moveCycle, cargoID, forceID,
                            cargoForce[BF.FORCE_INDEX_ZONE], force[BF.FORCE_INDEX_ZONE]);
    return;
  }

  // The move was invalid, not blocked.
  // So block the cargo and the transport to prevent further movement.
  obstruct.blocked[cargoID] = true;
  obstruct.blocked[forceID] = true;
}

function doTransportUnloadMove (
  game: Game, forceIndex: number, forceID: number, zoneID: number, x: number, y: number,
  cargoID: number, playerIndex: number, moveCycle: number, obstruct: Obstruct,
) {
  const force = game.force[playerIndex][forceIndex];
  const forceType = force[BF.FORCE_INDEX_TYPE];
  const forceFlags = game.forceFlags[forceType];

  const cargoIndex = bfH.getPlayerForceIndex(game, playerIndex, cargoID);
  if( cargoIndex < 0 ) {
    console.log('Unable to find the cargo to unload.');
    obstruct.invalid[forceID] = true;  // Flag transport to prevent further movement.
    return;
  }

  const cargoForce = game.force[playerIndex][cargoIndex];
  const cargoType = cargoForce[BF.FORCE_INDEX_TYPE];
  const cargoSpace = game.forceCargoSpace[cargoType];
  const liftSpace = game.forceLiftSpace[cargoType];
  const transportZoneID = force[BF.FORCE_INDEX_ZONE];

  if(cargoForce[BF.FORCE_INDEX_ZONE] !== BF.FORCE_ZONE_CARGO - forceID) {
    // Move is invalid, as cargo does not match up as being on the transport.
    obstruct.invalid[cargoID] = true;  // Flag cargo to prevent further movement.
    obstruct.invalid[forceID] = true;  // Flag transport to prevent further movement.
    return;
  }

  if(obstruct.blocked[forceID] && !(forceFlags & BF.FORCE_FLAG_CAN_UNLOAD_WHEN_BLOCKED)) {
    // Transport is blocked, and its type does not allow unloading when blocked,
    // so block the cargo and transport from further movement.
    bfTurnReplay.unloadBlocked(game, moveCycle, forceID, cargoID, transportZoneID, zoneID);
    obstruct.blocked[cargoID] = true;
    obstruct.blocked[forceID] = true;
    return;
  }

  const cargoTypeFlags = game.forceFlags[cargoType];

  if( forceFlags & BF.FORCE_FLAG_UNLOAD_SAME_ZONE ) {
    // Must unload into same zone.
    if( transportZoneID !== zoneID ) {
      // Move is invalid, as the zones do not match.
      obstruct.invalid[cargoID] = true;  // Flag cargo to prevent further movement.
      obstruct.invalid[forceID] = true;  // Flag transport to prevent further movement.
      return;
    }
    if( (!(cargoTypeFlags & BF.FORCE_FLAG_CAN_MOVE_ON_LAND) || !bfH.isLand(game, zoneID)) &&
        (!(cargoTypeFlags & BF.FORCE_FLAG_CAN_MOVE_ON_WATER) || bfH.isWater(game, zoneID)) ) {
      // Move is invalid, as the cargo is not allowed to move on destination zone type.
      obstruct.invalid[cargoID] = true;  // Flag cargo to prevent further movement.
      obstruct.invalid[forceID] = true;  // Flag transport to prevent further movement.
      return;
    }
  }
  else {
    // Must unload onto a neighbouring zone.
    if( (!(cargoTypeFlags & BF.FORCE_FLAG_CAN_MOVE_ON_LAND) ||
        !bfH.isLandNeighbour(game, transportZoneID, zoneID)) &&
        (!(cargoTypeFlags & BF.FORCE_FLAG_CAN_MOVE_ON_WATER) ||
        !bfH.isWaterNeighbour(game, transportZoneID, zoneID)) ) {
      // Move is invalid, as the cargo is not allowed to move on destination zone type, or the
      // destination is not a neighbour.
      obstruct.invalid[cargoID] = true;  // Flag cargo to prevent further movement.
      obstruct.invalid[forceID] = true;  // Flag transport to prevent further movement.
    }
  }

  // Unload the cargo/lift:
  const pos = confirmOrFixXY(game, zoneID, x, y);
  bfTurnReplay.move(game, moveCycle, cargoID, zoneID, pos.x, pos.y);
  if( (forceFlags & BF.FORCE_FLAG_CAN_MOVE_AFTER_UNLOADING) === 0 ) {
    force[BF.FORCE_INDEX_MOVE_POINTS] = 0;  // Transport cannot move after unloading.
  }

  if( game.forceCargoCapacity[forceType] > 0 && cargoSpace > 0 )
    force[BF.FORCE_INDEX_LOAD_POINTS] += cargoSpace;
  if( game.forceLiftCapacity[forceType] > 0 && liftSpace > 0 )
    force[BF.FORCE_INDEX_LIFT_POINTS] += liftSpace;
  obstruct.unloadedCargo[forceID] = true;  // Flag that it has unloaded something this turn.

  cargoForce[BF.FORCE_INDEX_ZONE_RETREAT] = 0;  // Unloaded cargo cannot retreat.
  cargoForce[BF.FORCE_INDEX_ZONE] = zoneID;
  cargoForce[BF.FORCE_INDEX_X] = pos.x;
  cargoForce[BF.FORCE_INDEX_Y] = pos.y;
  cargoForce[BF.FORCE_INDEX_MOVE_POINTS] = 0;  // Unloaded cargo cannot move any further.
}
